import React, { useEffect, useRef, useState } from 'react';

// Inject keyframes once globally — independent of Tailwind compilation
let __skbRevealKeyframesInjected = false;
const ensureKeyframes = () => {
  if (typeof document === 'undefined' || __skbRevealKeyframesInjected) return;
  const style = document.createElement('style');
  style.setAttribute('data-skb-reveal', 'true');
  style.textContent = `
    @keyframes skbReveal {
      0% { opacity: 0; transform: translate3d(0, 24px, 0); }
      100% { opacity: 1; transform: translate3d(0, 0, 0); }
    }
    [data-skb-reveal-hidden="true"] {
      opacity: 0;
      transform: translate3d(0, 24px, 0);
    }
    [data-skb-reveal-visible="true"] {
      animation: skbReveal 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards;
    }
    @media (prefers-reduced-motion: reduce) {
      [data-skb-reveal-hidden="true"],
      [data-skb-reveal-visible="true"] {
        opacity: 1 !important; transform: none !important; animation: none !important;
      }
    }
  `;
  document.head.appendChild(style);
  __skbRevealKeyframesInjected = true;
};

// Shared IntersectionObserver — much cheaper than one per <Reveal />
let __skbSharedObserver = null;
const __skbCallbacks = new WeakMap();
const getSharedObserver = () => {
  if (typeof window === 'undefined') return null;
  if (__skbSharedObserver) return __skbSharedObserver;
  __skbSharedObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const cb = __skbCallbacks.get(entry.target);
          if (cb) cb();
          __skbSharedObserver.unobserve(entry.target);
          __skbCallbacks.delete(entry.target);
        }
      }
    },
    { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
  );
  return __skbSharedObserver;
};

/**
 * Reveal — fades in once on viewport entry. Uses a single shared observer
 * + GPU-accelerated translate3d for smooth performance even on lower-end devices.
 */
const Reveal = ({ children, delay = 0, as: Tag = 'div', className = '', ...rest }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    ensureKeyframes();
    const el = ref.current;
    if (!el || visible) return;

    const observer = getSharedObserver();
    if (!observer) {
      setVisible(true);
      return;
    }
    __skbCallbacks.set(el, () => setVisible(true));
    observer.observe(el);
    return () => {
      observer.unobserve(el);
      __skbCallbacks.delete(el);
    };
  }, [visible]);

  const dataProps = visible
    ? { 'data-skb-reveal-visible': 'true' }
    : { 'data-skb-reveal-hidden': 'true' };

  return (
    <Tag
      ref={ref}
      className={className}
      style={visible && delay ? { animationDelay: `${delay}ms` } : undefined}
      {...dataProps}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
